// components/NotificationBell/NotificationStack.tsx
import React, { useState, useEffect, useRef } from 'react';
import { UserInfoNotification } from '../../types/notifications';
import { CustomNotificationPopup } from './CustomNotificationPopup';
import { useTheme } from '../../contexts/ThemeContext';
import { createPortal } from 'react-dom';
import { InboxOutlined } from '@ant-design/icons';

interface NotificationStackProps {
  notifications: UserInfoNotification[];
  onResolve: (id: string) => void;
  onClose: (id: string) => void;
  onOpenDrawer?: () => void;
}

const MAX_VISIBLE_NOTIFICATIONS = 4;
const NOTIFICATION_HEIGHT = 140;
const INITIAL_TOP = 80;

interface NotificationPosition {
  id: string;
  top: number;
  notification: UserInfoNotification;
}

export const NotificationStack: React.FC<NotificationStackProps> = ({
  notifications,
  onResolve,
  onClose,
  onOpenDrawer
}) => {
  const { theme } = useTheme();
  const [positions, setPositions] = useState<NotificationPosition[]>([]);
  const positionsRef = useRef<Map<string, number>>(new Map());
  const [showMoreCard, setShowMoreCard] = useState(false);

  useEffect(() => {
    // Calcular posiciones para las notificaciones
    const visibleNotifs = notifications.slice(0, MAX_VISIBLE_NOTIFICATIONS);
    const currentIds = new Set(visibleNotifs.map(n => n.id));

    // Detectar notificaciones que fueron removidas
    const removedIds = Array.from(positionsRef.current.keys()).filter(id => !currentIds.has(id));

    // Limpiar posiciones de notificaciones que ya no existen
    removedIds.forEach(id => positionsRef.current.delete(id));

    // Si se removió alguna notificación, reorganizar todas para llenar espacios
    const shouldReorganize = removedIds.length > 0;

    const newPositions: NotificationPosition[] = [];

    if (shouldReorganize) {
      // Reorganizar todas las posiciones para que sean consecutivas
      visibleNotifs.forEach((notif, index) => {
        const newTop = INITIAL_TOP + (index * NOTIFICATION_HEIGHT);
        positionsRef.current.set(notif.id, newTop);
        newPositions.push({ id: notif.id, top: newTop, notification: notif });
      });
    } else {
      // Mantener posiciones existentes y solo asignar a nuevas
      visibleNotifs.forEach((notif) => {
        if (positionsRef.current.has(notif.id)) {
          // Mantener posición existente
          const existingTop = positionsRef.current.get(notif.id)!;
          newPositions.push({ id: notif.id, top: existingTop, notification: notif });
        } else {
          // Calcular posición para notificación nueva - encontrar el primer hueco disponible
          let targetTop = INITIAL_TOP;
          const usedTops = new Set(newPositions.map(p => p.top));

          while (usedTops.has(targetTop)) {
            targetTop += NOTIFICATION_HEIGHT;
          }

          positionsRef.current.set(notif.id, targetTop);
          newPositions.push({ id: notif.id, top: targetTop, notification: notif });
        }
      });

      // Ordenar por posición para mantener el orden visual
      newPositions.sort((a, b) => a.top - b.top);
    }

    setPositions(newPositions);

    // Mostrar card de "ver más" si hay notificaciones ocultas
    if (notifications.length > MAX_VISIBLE_NOTIFICATIONS) {
      setTimeout(() => setShowMoreCard(true), 50);
    } else {
      setShowMoreCard(false);
    }
  }, [notifications]);

  const remainingCount = notifications.length - MAX_VISIBLE_NOTIFICATIONS;
  const moreCardTop = INITIAL_TOP + (MAX_VISIBLE_NOTIFICATIONS * NOTIFICATION_HEIGHT);

  return (
    <>
      {positions.map((pos) => (
        <CustomNotificationPopup
          key={pos.id}
          notification={pos.notification}
          top={pos.top}
          onResolve={onResolve}
          onClose={onClose}
          theme={theme}
        />
      ))}

      {/* Card de "Ver más notificaciones" */}
      {remainingCount > 0 && onOpenDrawer && createPortal(
        <div
          onClick={onOpenDrawer}
          style={{
            position: 'fixed',
            top: `${moreCardTop}px`,
            right: showMoreCard ? '24px' : '-500px',
            width: '480px',
            backgroundColor: theme === 'dark' ? '#1f1f1f' : '#fff',
            borderRadius: '8px',
            boxShadow: theme === 'dark'
              ? '0 4px 12px rgba(0, 0, 0, 0.5)'
              : '0 4px 12px rgba(0, 0, 0, 0.15)',
            padding: '16px',
            zIndex: 9999 - MAX_VISIBLE_NOTIFICATIONS,
            transition: 'right 0.3s ease-in-out',
            border: `2px dashed ${theme === 'dark' ? '#444' : '#d9d9d9'}`,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            minHeight: '70px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#1890ff';
            e.currentTarget.style.backgroundColor = theme === 'dark' ? '#2a2a2a' : '#f0f5ff';
            const icon = e.currentTarget.querySelector('.inbox-icon') as HTMLElement;
            if (icon) {
              icon.style.color = '#1890ff';
              icon.style.opacity = '1';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = theme === 'dark' ? '#444' : '#d9d9d9';
            e.currentTarget.style.backgroundColor = theme === 'dark' ? '#1f1f1f' : '#fff';
            const icon = e.currentTarget.querySelector('.inbox-icon') as HTMLElement;
            if (icon) {
              icon.style.color = theme === 'dark' ? '#999' : '#666';
              icon.style.opacity = '0.6';
            }
          }}
        >
          <InboxOutlined
            className="inbox-icon"
            style={{
              fontSize: '32px',
              opacity: 0.6,
              color: theme === 'dark' ? '#999' : '#666',
              transition: 'all 0.3s',
            }}
          />
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{
              fontSize: '16px',
              fontWeight: 600,
              marginBottom: '4px',
              color: theme === 'dark' ? '#fff' : '#000',
            }}>
              +{remainingCount} notificación{remainingCount > 1 ? 'es' : ''} más
            </div>
            <div style={{
              fontSize: '13px',
              color: theme === 'dark' ? '#999' : '#666',
            }}>
              Haz click para ver todas
            </div>
          </div>
          <div style={{
            fontSize: '20px',
            opacity: 0.6,
          }}>
            →
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

NotificationStack.displayName = 'NotificationStack';
